<script setup>
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Errors from './Errors';
import { flatten } from '/app/utils/helpers';
import { sequence } from '@bignerve/ui-utils';
import { deferredPromise } from '/app/utils/promise';
import { ref, reactive, computed, watch, provide, getCurrentInstance, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { FormKey } from '/app/composables/form';
import { api } from '/app/plugins/services';
import NNotify from '/app/components/NNotify.vue';

const props = defineProps({
	action: {
		type: [String, Function],
	},
	method: {
		type: String,
		default: 'POST',
	},
	data: {
		type: Object,
		default: () => ({}),
	},
	confirm: {
		type: Boolean,
		default: false,
	},
	once: {
		type: Boolean,
		default: false,
	},
	dirtable: {
		type: Array,
	},
	prune: {
		type: Function,
		default: (v) => v,
	},
	beforeSubmit: Function,
});

const emit = defineEmits([
	'change',
	'before-send',
	'after-send',
	'submit',
	'reset',
	'error',
	'success',
	'modal-closed',
]);

const { t } = useI18n();

/** Keep a plain object so injected `form.fields` is never undefined (v-models expect it). */
function normalizeFields(data) {
	return data ?? {};
}

const dialogRef = ref(null);

const busy = ref(false);
const success = ref(false);
const hasErrors = ref(false);
const errors = ref(new Errors());
const fields = ref(normalizeFields(props.data));
const formFields = ref([]);
const middlewares = ref(new Map());
const sendPromise = ref(null);

watch(
	() => props.data,
	(n) => {
		fields.value = normalizeFields(n);
	},
	{ deep: true },
);

watch(
	fields,
	(n) => {
		emit('change', n);
	},
	{ deep: true },
);

const dirty = computed(() => {
	const f = fields.value ?? {};
	if (props.dirtable) {
		return props.dirtable.some((field) => !isEmpty(get(f, field, null)));
	}

	return Object.keys(flatten(f)).length;
});

function submit() {
	if (props.once && (busy.value || success.value)) {
		return;
	}

	busy.value = false;
	success.value = false;
	errors.value.clear();
	hasErrors.value = false;
	sendPromise.value = deferredPromise();

	if (!validate()) {
		return {
			success: false,
			message: 'Validation failed',
		};
	}

	if (props.confirm) {
		dialogRef.value?.open();

		return sendPromise.value.promise;
	}

	send();

	return sendPromise.value.promise;
}

async function send() {
	if (props.confirm) {
		closeDialog();
	}

	if (!props.action) {
		console.warn('No action provided');

		sendPromise.value?.resolve({
			success: false,
			message: 'No action provided',
		});

		return;
	}

	busy.value = true;
	errors.value.clear();

	try {
		await sequence([...middlewares.value.values()], (middleware) => middleware(fields.value));
	} catch (error) {
		console.error(error);

		sendPromise.value?.resolve({
			success: false,
			message: 'Middleware failed',
		});
	}

	const candSend = props.beforeSubmit ? await props.beforeSubmit(fields.value) : true;

	if (!candSend) {
		busy.value = false;

		sendPromise.value?.resolve({
			success: false,
			message: 'Before submit callback returned false',
		});

		return;
	}

	try {
		emit('before-send', fields.value);

		const pruned = props.prune(fields.value);

		const payload = await (typeof props.action === 'function'
			? props.action(pruned)
			: api.requester.request({ method: props.method, url: props.action, data: pruned }).then((r) => r.data)
		);

		emit('after-send', payload);

		onSuccess(payload);

		sendPromise.value?.resolve({
			success: true,
			payload,
		});
	} catch (error) {
		onError(error);

		sendPromise.value?.resolve({
			success: false,
			message: error.message,
		});
	} finally {
		emit('submit');
	}
}

function reset() {
	busy.value = false;
	success.value = false;
	fields.value = normalizeFields(props.data);
	hasErrors.value = false;
	errors.value.clear();

	formFields.value.forEach((field) => {
		field.proxy?.reset?.();
	});

	emit('reset');
}

function onSuccess(data) {
	hasErrors.value = false;
	busy.value = false;
	success.value = true;
	errors.value.clear();
	emit('success', data, fields.value);
}

function onError(error) {
	const errList = get(error, 'response.data.errors', []);

	busy.value = false;
	success.value = false;
	hasErrors.value = true;
	errors.value.record(errList);
	emit('error', errList);
}

function closeDialog() {
	dialogRef.value?.close();
}

function validate() {
	const invalidFields = formFields.value.filter((field) => field.required && !field.modelValue);

	errors.value.record(
		invalidFields.map((field) => ({
			field: field.name,
			error: t(
				'component.form.error.required',
				field.label || field.fieldName ? 0 : 1,
				{ field: field.label || field.fieldName },
			),
		})),
	);

	hasErrors.value = invalidFields.length > 0;

	return !hasErrors.value;
}

function handleConfirm() {
	send();
}

function handleCancel() {
	emit('modal-closed');

	sendPromise.value?.resolve({
		success: false,
		message: t('component.form.user-cancelled'),
	});
}

defineExpose({ fields, errors, busy, success, dirty, formFields, submit, validate, reset });

const instance = getCurrentInstance();
const attrs = useAttrs();

// Pure reactive object — Vue can fully track all dependencies.
// Getters for $parent/$attrs delegate to the component instance.
provide(FormKey, reactive({
	fields,
	errors,
	busy,
	success,
	dirty,
	formFields,
	middlewares,
	submit,
	validate,
	reset,
	get $parent() { return instance?.parent?.proxy; },
	get $attrs() { return attrs; },
}));
</script>

<template>
	<form @submit.prevent="submit" @keypress.enter.prevent>
		<NNotify
			v-if="confirm"
			ref="dialogRef"
			:title="$t('component.form.confirm-title')"
			:message="$t('component.form.confirm-message')"
			@cancel="handleCancel"
			@confirm="handleConfirm"
		/>
		<slot
			:fields="fields"
			:errors="errors"
			:dirty="dirty"
			:busy="busy"
			:success="success"
			:submit="submit"
			:reset="reset"
		/>
	</form>
</template>
